import React from 'react';
import { Link } from 'react-router-dom';

import Layout from '../components/layout/Layout';

// Export empty object to ensure file is treated as a module
export {};

const HowItWorks: React.FC = () => {
  return (
    <Layout>
      <div className="bg-gradient-to-br from-dark to-dark-dark text-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4 text-primary">How Does This Work?</h1>
            <p className="text-xl max-w-3xl mx-auto">
              Your complete guide to using SpinDom - create, share, and track wheels for maximum
              fun.
            </p>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden mb-12">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">Getting Started</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">What is SpinDom?</h3>
                  <p className="text-gray-300">
                    SpinDom is a platform that lets you create customizable spinning wheels for
                    various purposes. Whether you&apos;re looking to make decisions, assign tasks,
                    create interactive experiences, our wheels provide a fun and engaging way to do
                    it.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">Creating Your First Wheel</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-300 ml-4">
                    <li>Click on &quot;Create Wheel&quot; in the navigation menu</li>
                    <li>Give your wheel a name</li>
                    <li>Add entries (items that will appear on your wheel)</li>
                    <li>Customize colors and settings if desired</li>
                    <li>Save your wheel</li>
                  </ol>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">Spinning a Wheel</h3>
                  <p className="text-gray-300">
                    Once your wheel is created, simply click the &quot;Spin Wheel&quot; button to
                    spin. The wheel will rotate and eventually stop on a random entry. The result
                    will be displayed in a popup that you can dismiss or use to spin again.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden mb-12">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">User Tiers</h2>

              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold mb-2 flex items-center">
                    <span className="inline-block w-8 h-8 bg-gray-500 rounded-full mr-3 flex items-center justify-center text-white">
                      1
                    </span>
                    Guest (Free)
                  </h3>
                  <div className="ml-11 text-gray-300">
                    <p className="mb-2">Our basic tier that requires no account:</p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Create unlimited wheels</li>
                      <li>Basic wheel customization</li>
                      <li>Local storage only (wheels saved in your browser)</li>
                      <li>No spin tracking or history</li>
                      <li>No cloud saving (wheels will be lost if you clear browser data)</li>
                    </ul>
                    <p className="mt-2 text-sm italic">
                      Perfect for casual users who just want to create and spin wheels without
                      saving data.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2 flex items-center">
                    <span className="inline-block w-8 h-8 bg-primary rounded-full mr-3 flex items-center justify-center text-white">
                      2
                    </span>
                    Signed-In (Free)
                  </h3>
                  <div className="ml-11 text-gray-300">
                    <p className="mb-2">Create a free account to unlock these features:</p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Everything in the Guest tier</li>
                      <li>Save unlimited wheels to your account</li>
                      <li>Cloud synchronization (access your wheels from any device)</li>
                      <li>Share wheels with spin tracking</li>
                      <li>Spin history and statistics</li>
                      <li>Advanced wheel customization options</li>
                    </ul>
                    <p className="mt-2 text-sm italic">
                      Great for enthusiasts who want to save and share their favorite wheels.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden mb-12">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">Sharing & Tracking Spins</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">How to Share a Wheel</h3>
                  <p className="text-gray-300 mb-4">
                    When logged in, you can share your wheels with others and track their spins:
                  </p>
                  <ol className="list-decimal list-inside space-y-2 text-gray-300 ml-4">
                    <li>Navigate to &quot;My Wheels&quot; in your account</li>
                    <li>Select the wheel you want to share</li>
                    <li>Click the &quot;Share&quot; button</li>
                    <li>
                      Configure sharing options:
                      <ul className="list-disc list-inside ml-6 mt-1">
                        <li>Set a spin limit (maximum number of spins allowed)</li>
                        <li>Set an expiration date (when the share link will expire)</li>
                        <li>Enable/disable spin tracking</li>
                        <li>Add an optional access code for extra security</li>
                      </ul>
                    </li>
                    <li>Click &quot;Create Share Link&quot; to generate a unique URL</li>
                    <li>Copy and share the link with anyone you want</li>
                  </ol>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">Tracking Spins</h3>
                  <p className="text-gray-300">
                    Once you&apos;ve shared a wheel with spin tracking enabled:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>Anyone with the link can spin the wheel</li>
                    <li>Each spin is recorded with the result and timestamp</li>
                    <li>You can view all spin history in the shared wheel dashboard</li>
                    <li>You can see who spun the wheel (if they were logged in)</li>
                    <li>You can even spin the wheel remotely for them from your dashboard</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">Share Link Management</h3>
                  <p className="text-gray-300">You have full control over your shared wheels:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>Deactivate a share link at any time</li>
                    <li>Extend or reduce the expiration date</li>
                    <li>Increase or decrease the spin limit</li>
                    <li>Create multiple share links for the same wheel with different settings</li>
                    <li>Delete share links permanently</li>
                  </ul>
                  <p className="mt-2 text-sm italic">
                    Note: All signed-in users can create and share wheels with full tracking
                    capabilities.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">Tips & Advanced Features</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">Customizing Your Wheels</h3>
                  <p className="text-gray-300">
                    Make your wheels unique with these customization options:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-300 ml-4">
                    <li>Add emojis to wheel entries for visual appeal</li>
                    <li>Customize colors for individual wheel segments</li>
                    <li>Adjust spin duration and animation speed</li>
                    <li>Set weighted probabilities for certain outcomes (premium feature)</li>
                    <li>Apply different themes to match your style</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">Account Management</h3>
                  <p className="text-gray-300">Take control of your account:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-300 ml-4">
                    <li>Update your profile information in the Profile page</li>
                    <li>Change your password regularly for security</li>
                    <li>Manage your account settings and preferences</li>
                    <li>Export your wheel data for backup (premium feature)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">Privacy & Security</h3>
                  <p className="text-gray-300">We take your privacy seriously:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-300 ml-4">
                    <li>Your wheels are private by default</li>
                    <li>Only people with your share links can access shared wheels</li>
                    <li>Access codes provide an extra layer of security</li>
                    <li>You can delete your data at any time</li>
                    <li>We use secure authentication and data storage</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/create-wheel" className="btn-primary px-8 py-3 text-lg">
                Create a Wheel
              </Link>
              <Link to="/sign-up" className="btn-secondary px-8 py-3 text-lg">
                Create Account
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HowItWorks;
